import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from 'react-helmet-async';
import App from "./App.tsx";
import "./index.css";

// Disable browser scroll restoration - we handle it manually
if ('scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

// Render app immediately for faster FCP
const rootElement = document.getElementById("root")!;

// Use scheduler to break up non-critical tasks
const scheduler = typeof requestIdleCallback !== 'undefined' 
  ? requestIdleCallback 
  : (cb: IdleRequestCallback) => setTimeout(cb, 1);

const scheduleAfterLoad = (task: IdleRequestCallback, timeout: number, delayMs: number) => {
  const queueTask = () => {
    window.setTimeout(() => scheduler(task, { timeout }), delayMs);
  };

  if (document.readyState === 'complete') {
    window.setTimeout(queueTask, 0);
    return;
  }

  window.addEventListener('load', () => window.setTimeout(queueTask, 0), { once: true });
};

createRoot(rootElement).render(
  <StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </StrictMode>
);

// Defer device detection until after the page has settled.
scheduleAfterLoad(() => {
  import("./utils/deviceDetection").then(({ initDeviceDetection }) => {
    initDeviceDetection();
  });
}, 3000, 7000);

// Visitor tracking and performance monitoring should not compete with mobile LCP.
scheduleAfterLoad(() => {
  import("./utils/visitorIntent")
    .then(({ initVisitorTracking }) => initVisitorTracking())
    .catch((err) => console.error("Failed to load visitor tracking:", err));
}, 3000, 9000);

scheduleAfterLoad(() => {
  import("./utils/performanceMonitoring")
    .then(({ initPerformanceMonitoring }) => initPerformanceMonitoring())
    .catch((err) => console.error("Failed to load performance monitoring:", err));
}, 3000, 11000);
